import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import StructureTab from "./ClientDetail/StructureTab";
import DevBannerPreview from "./ClientDetail/DevBannerPreview";
import DevTestChecklist from "./ClientDetail/DevTestChecklist";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface ClientsTabsPreviewDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  client: any;
  isEditMode: boolean;
  isLoading?: boolean;
  onRefresh?: () => void;
}

/**
 * Drawer para preview de abas de cliente
 * Renderiza ClientsWithTabs dentro de um drawer
 * Mostra DevBanner com observabilidade
 */
export default function ClientsTabsPreviewDrawer({
  isOpen,
  onOpenChange,
  client,
  isEditMode,
  isLoading = false,
  onRefresh,
}: ClientsTabsPreviewDrawerProps) {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh] flex flex-col">
        <DrawerHeader className="border-b">
          <DrawerTitle>Preview de Abas - {client?.nomeFantasia || "Novo Cliente"}</DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto">
          {/* DevBanner com observabilidade */}
          <div className="p-4 border-b bg-amber-50">
            <DevBannerPreview
              isEditMode={isEditMode}
              client={client}
              effectiveIsMatriz={client?.isMatriz || false}
            />
          </div>

          {/* Abas */}
          <div className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                <TabsTrigger value="structure" disabled={!isEditMode}>
                  Matriz e Filiais
                </TabsTrigger>
              </TabsList>

              {/* Aba Visão Geral */}
              <TabsContent value="overview" className="space-y-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Este é um preview UI-only das abas. Sem alterações no backend.
                  </AlertDescription>
                </Alert>
                
                {/* Checklist de teste manual */}
                <div className="mt-6">
                  <DevTestChecklist
                    devTabsVisible={true}
                    previewButtonVisible={true}
                    previewLineButtonVisible={true}
                    tabsVisible={true}
                    simulateMatrizWorking={true}
                  />
                </div>
              </TabsContent>

              {/* Aba Matriz e Filiais */}
              <TabsContent value="structure" className="space-y-4">
                {!isEditMode ? (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Salve o cliente primeiro para acessar a aba Matriz e Filiais
                    </AlertDescription>
                  </Alert>
                ) : (
                  <StructureTab
                    company={client}
                    isLoading={isLoading}
                    onRefresh={onRefresh}
                  />
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
